<template>
  <div class="gmap">
    <gmap-map :center="mapCenter" :zoom="16" ref="gmap" :options="mapOptions" map-type-id="roadmap" class="gmap__map">
      <transition name="fade">
        <router-view
          v-if="
            map &&
              mapBounds &&
              visibleLocations &&
              $route.name !== 'zones' &&
              $route.name !== 'divisionZones' &&
              !isZonesIntentionView
          "
          :zones="[]"
          :map="map"
          :locations="computedLocations"
          :source="currentSource"
          :active-filters="activeFilters"
          :filters="brandFilters[currentKpi].filters"
          :current-settings="computedSettings"
          @setZoomDefault="setZoomDefault"
          @resetZoom="resetZoom"
        />
        <router-view
          v-else-if="map && mapBounds && visibleLocations && brandZonesCurrentKPI"
          :map="map"
          :filters="brandFilters[currentKpi].filters"
          :zones="brandZonesCurrentKPI"
          @resetZoom="resetZoom"
        />
      </transition>
    </gmap-map>
    <!-- Location Sidebar -->
    <transition :name="isMobile ? 'slide-left' : 'scale-tl'">
      <gmap-location
        class="gmap__location"
        v-if="currentLocation && !noUi && !isMarkersIntentionView"
        :location="currentLocation"
        :source="currentSource"
        :settings="computedSettings"
        :filters="brandFilters[currentKpi].filters"
        :distance="currentDistance"
        @refreshLocations="$emit('refreshLocations', false)"
      />
      <gmap-location-intention
        class="gmap__location"
        v-if="currentLocation && !noUi && isMarkersIntentionView"
        :location="currentLocation"
        :source="currentSource"
        :settings="computedSettings"
        :filters="brandFilters[currentKpi].filters"
        :distance="currentDistance"
        @refreshLocations="$emit('refreshLocations', false)"
      />
    </transition>
    <!-- Zone Sidebar -->
    <transition :name="isMobile ? 'slide-left' : 'scale-tl'">
      <gmap-zone
        class="gmap__zone"
        v-if="currentZone && !noUi && !isZonesIntentionView"
        :zone="currentZone"
        :filters="brandFilters[currentKpi].filters"
      />
    </transition>
    <!-- Zone Intention Sidebar -->
    <transition :name="isMobile ? 'slide-left' : 'scale-tl'">
      <gmap-zone-intention
        class="gmap__zone-intention"
        v-if="currentZone && !noUi && isZonesIntentionView"
        :zone="currentZone"
        :location="locationRelatedToCurrentZone"
        :filters="brandFilters[currentKpi].filters"
      />
    </transition>
    <!-- LEVEL 1 -->
    <transition name="fade">
      <ui-button
        class="gmap__level-1-button"
        :label="$t('radar.actions.level1')"
        button="cta"
        icon="previous"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('radar.actions.level1'),
          offset: 3,
        }"
        @click.prevent="goToLevel1View()"
        v-if="mapBounds && $route.name === 'divisionZonesIntention'"
      />
    </transition>
    <!-- Filters Button -->
    <transition name="fade">
      <ui-button
        class="gmap__filters-button"
        :label="$t('radar.actions.filters')"
        button="cta"
        icon="filter"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('radar.actions.filters'),
          offset: 3,
        }"
        @click.prevent="onClickToggleFilters"
        v-if="mapBounds && !filters.active && !noUi"
      />
    </transition>
    <!-- Filters Sidebar -->
    <transition :name="isMobile ? 'slide-right' : 'scale-tr'">
      <filters
        class="gmap__filters"
        v-show="filters.active && !noUi"
        @refreshLocations="$emit('refreshLocations')"
        @refreshFilters="refreshFilters"
        @refreshAlgoliaFilters="refreshAlgoliaFilters"
      />
    </transition>
    <!-- Legend Button -->
    <transition name="fade">
      <ui-button
        class="gmap__legend-button"
        :label="$t('radar.actions.legend')"
        button="cta"
        icon="map-legend"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('radar.actions.legend'),
          offset: 3,
        }"
        @click.prevent="toggleLegends(true)"
        v-if="isMarkersIntentionView && mapBounds && !legends.active && !noUi && legendsAvailable.length > 0"
      />
    </transition>
    <!-- Legend Sidebar -->
    <transition name="slide-right">
      <legends
        class="gmap__legends"
        v-show="isMarkersIntentionView && legends.active && !noUi"
        :legends="legendsAvailable"
      />
    </transition>
    <!-- Timeline Button -->
    <transition name="fade">
      <ui-button
        class="gmap__timeline-button"
        :class="{ 'gmap__timeline-button--shift': filters.active }"
        :label="$t('gmap.actions.timeline')"
        button="cta"
        icon="timeline"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('gmap.actions.timeline'),
          offset: 3,
        }"
        v-if="mapBounds && !timeline.active && !noUi && timelineKpi.length > 1"
        @click.prevent="toggleTimeline(true)"
      />
    </transition>
    <!-- Timeline Sidebar -->
    <transition :name="isMobile ? 'slide-bottom' : 'slide-top'">
      <timeline
        class="gmap__timeline"
        v-show="timeline.active && !noUi"
        :timeline-kpi="timelineKpi"
        @refreshLocations="$emit('refreshLocations')"
      />
    </transition>
    <!-- Search Button -->
    <transition name="fade">
      <ui-button
        class="gmap__search-button"
        :class="{
          'gmap__search-button--shift': currentLocation,
          'gmap__search-button--left-2': $route.name === 'divisionZonesIntention',
        }"
        :label="$t('gmap.actions.search')"
        button="cta"
        icon="search"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('gmap.actions.search'),
          offset: 3,
        }"
        @click.prevent="toggleElements"
        v-if="(!search.active || isMobile) && mapBounds && !noUi && !isMarkersIntentionView"
      />
    </transition>
    <!-- Search Input -->
    <gmap-search
      class="gmap__search"
      :class="{
        'gmap__search--expanded': search.active,
        'gmap__search--left-2': $route.name === 'divisionZonesIntention',
      }"
      ref="search"
      :locations="searchLocationsCurrentKpi"
    />
    <!-- Stack Popup -->
    <transition :name="isMobile ? 'slide-right' : 'scale-tl'">
      <gmap-stack class="gmap__stack" v-if="stack.popUp === true"></gmap-stack>
    </transition>
    <!-- Stack Button -->
    <transition name="fade">
      <ui-button
        :class="{ 'gmap__stack-button--shift': currentLocation }"
        class="gmap__stack-button"
        :label="$t('gmap.actions.stacks')"
        button="cta"
        icon="stack"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('gmap.actions.stacks'),
          offset: 3,
        }"
        @click.prevent="onClickToggleStackPopUp(true)"
        v-if="isStackButtonDisplay"
      />
    </transition>
    <!-- Compare Popup -->
    <transition :name="isMobile ? 'slide-right' : 'scale-tl'">
      <gmap-compare class="gmap__compare" v-if="compare.active === true" :zone="currentZone"> </gmap-compare>
    </transition>
    <!-- Compare Button -->
    <transition name="fade" v-if="mapBounds && !noUi && computedCompare && computedSettings.with_zone">
      <ui-button
        :class="{
          'gmap__compare-button--shift': currentLocation,
          'gmap__compare-button--swap': isStackButtonDisplay && !isMobile,
        }"
        class="gmap__compare-button"
        :label="$t('gmap.actions.compare')"
        button="cta"
        icon="compare"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('gmap.actions.compare'),
          offset: 3,
        }"
        @click.prevent="onClickToggleCompare(!compare.active)"
        v-if="(!compare.active || isMobile) && mapBounds && !noUi"
      />
    </transition>
    <!-- Settings Button -->
    <transition name="fade">
      <ui-button
        class="gmap__settings-button"
        :label="$t('radar.actions.settings')"
        button="cta"
        icon="settings"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'hover',
          content: $t('radar.actions.settings'),
          offset: 3,
        }"
        v-if="mapBounds && !noUi"
        @click.prevent="openSettings"
      />
    </transition>
    <!-- Settings Modal -->
    <settings @updateMapOptions="updateMapOptions" />
    <!-- Disclaimer Mobile -->
    <transition name="fade">
      <ui-button
        class="gmap__disclaimer-mobile"
        :class="{
          'gmap__disclaimer-mobile--all': visibleLocations.length <= settings.marker.max,
          'gmap__disclaimer-mobile--partial': visibleLocations.length > settings.marker.max && !filtersIsUpdate,
          'gmap__disclaimer-mobile--update': visibleLocations.length > settings.marker.max && filtersIsUpdate,
        }"
        :label="$t('gmap.disclaimer.partial', { count: settings.marker.max, max: visibleLocations.length })"
        button="cta"
        :icon="visibleLocations.length > settings.marker.max ? 'priority' : 'check'"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'click',
          content:
            visibleLocations.length > settings.marker.max
              ? $t('gmap.disclaimer.partial', { count: settings.marker.max, max: visibleLocations.length })
              : $t('gmap.disclaimer.all', { max: visibleLocations.length }),
          offset: 3,
        }"
        v-if="visibleLocations && mapBounds && $route.name !== 'zones' && !isIntentionView && !noUi"
      />
    </transition>
    <!-- Disclaimer Desktop -->
    <transition name="fade">
      <div
        class="gmap__disclaimer"
        :class="{
          'gmap__disclaimer--all': visibleLocations.length <= settings.marker.max,
          'gmap__disclaimer--partial': visibleLocations.length > settings.marker.max && !filtersIsUpdate,
          'gmap__disclaimer--update': visibleLocations.length > settings.marker.max && filtersIsUpdate,
        }"
        v-if="visibleLocations && mapBounds && $route.name !== 'zones' && !isIntentionView && !noUi"
      >
        <span
          class="gmap__disclaimer__label"
          v-if="visibleLocations.length > settings.marker.max"
          v-html="$t('gmap.disclaimer.partial', { count: settings.marker.max, max: visibleLocations.length })"
        >
        </span>
        <span
          class="gmap__disclaimer__label"
          v-else
          v-html="$t('gmap.disclaimer.all', { max: visibleLocations.length })"
        ></span>
      </div>
    </transition>
    <!-- Information current display Mobile -->
    <transition name="fade">
      <ui-button
        class="gmap__disclaimer-mobile gmap__disclaimer-mobile--warning"
        button="cta"
        label=""
        icon="priority"
        :iconOnly="true"
        v-tooltip="{
          placement: 'top',
          trigger: 'click',
          content: `
            <div>
              ${$t('gmap.info.max', { marker: settingMarker.nbMaxDisplay, nbHits: settingMarker.nbHits })}
            </div>
          `,
          offset: 3,
        }"
        v-if="mapBounds && isMarkersIntentionView && !noUi && settingMarker.nbHits > settingMarker.nbMaxDisplay"
      />
    </transition>
    <!-- Information current display Desktop -->
    <transition name="fade">
      <div
        v-if="mapBounds && isMarkersIntentionView && !noUi && settingMarker.nbHits > settingMarker.nbMaxDisplay"
        class="gmap__disclaimer gmap__disclaimer--warning"
      >
        <div class="gmap__disclaimer__info">
          <div v-html="$t('gmap.info.max', { marker: settingMarker.nbMaxDisplay, nbHits: settingMarker.nbHits })" />
        </div>
      </div>
    </transition>
    <!-- Reset view -->
    <transition name="fade">
      <router-link
        @click.native="toggleCompare(false)"
        tag="div"
        :to="{
          name: $route.name === 'division' ? 'markers' : 'zones',
          params: { id: $route.params.id },
          query: $route.query,
        }"
        v-slot="{ navigate }"
        v-if="
          mapBounds &&
            ($route.name === 'location' || $route.name === 'division' || $route.name === 'divisionZones') &&
            !noUi
        "
      >
        <ui-button
          class="gmap__reset-button"
          :class="{ 'gmap__reset-button--translate': search.active || currentLocation !== null }"
          :label="$t('gmap.actions.reset')"
          button="cta"
          icon="reset"
          @click="navigate"
        />
      </router-link>
    </transition>
    <!-- Geographical Filter -->
    <gmap-geo
      v-if="
        !noUi &&
          $route.name !== 'location' &&
          $route.name !== 'division' &&
          $route.name !== 'divisionZones' &&
          (subDivisions[currentKpi].subdivisions.length > 1 || sectors.length > 1)
      "
      @refreshCountries="$emit('refreshCountries', true)"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions, mapState } from 'vuex'
import UiButton from '@/components/UI/Button.vue'
import GmapLocation from '@/components/Gmap/Location.vue'
import GmapLocationIntention from '@/components/Gmap/LocationIntention.vue'
import GmapZone from '@/components/Gmap/Zone.vue'
import GmapZoneIntention from '@/components/Gmap/ZoneIntention.vue'
import Filters from '@/components/Filters.vue'
import Settings from '@/components/Settings.vue'
import Timeline from '@/components/Timeline.vue'
import GmapSearch from '@/components/Gmap/Search.vue'
import GmapCompare from '@/components/Gmap/Compare.vue'
import GmapStack from '@/components/Gmap/Stack.vue'
import GmapGeo from '@/components/Gmap/Geo.vue'
import Legends from '@/components/Legends.vue'
import { isMobile, isDesktop } from '@/utils/navigator.util'
import { launchGAEvent } from '@/utils/gtm.util'

export default {
  name: 'Gmap',
  components: {
    UiButton,
    GmapLocation,
    GmapLocationIntention,
    Filters,
    Settings,
    Timeline,
    GmapSearch,
    GmapCompare,
    GmapStack,
    GmapZone,
    GmapZoneIntention,
    GmapGeo,
    Legends,
  },
  props: {
    previousRoute: {
      type: Object,
      required: false,
      default: null,
    },
    nextRoute: {
      type: Object,
      required: false,
      default: null,
    },
  },
  data() {
    return {
      map: null,
      filtersIsUpdate: false,
      isMobile: isMobile(),
      isDesktop: isDesktop(),
    }
  },
  watch: {
    visibleLocations: {
      handler(current, old) {
        if (current?.length === old?.length && this.filtersIsUpdate) {
          this.mapFitBounds(this.map)
        }
      },
    },
    'map.zoom': {
      handler() {
        this.setCurrentZoom(this.map.zoom)
      },
    },
  },
  mounted() {
    if (this.isDesktop && !this.isMarkersIntentionView) {
      this.toggleFilters(true)
    }
    this.$refs.gmap.$mapPromise.then(map => {
      this.mapFitBounds(map)
      const controlsWrapper = document.createElement('div')
      controlsWrapper.className = 'gmap__map__zoom-controls'
      controlsWrapper.id = 'map-controls'
      if (this.$route.query.marker_all || this.noUi) {
        controlsWrapper.className += ' gmap__map__zoom-controls--hidden'
      }
      if (this.$route.query.marker_all) {
        map.setOptions({ draggable: false, scrollwheel: false, disableDoubleClickZoom: true })
      }

      const controlsContainer = document.createElement('div')
      controlsContainer.className = 'gmap__map__zoom-controls__container'

      const zoomInButton = document.createElement('div')
      zoomInButton.className = 'gmap__map__zoom-controls__container__zoom-in backoffice-icons'
      zoomInButton.innerHTML = 'add'

      // Set Class for the zoomOut
      const zoomOutButton = document.createElement('div')
      zoomOutButton.className = 'gmap__map__zoom-controls__container__zoom-out backoffice-icons'
      zoomOutButton.innerHTML = 'remove'

      controlsWrapper.appendChild(controlsContainer)
      controlsContainer.appendChild(zoomInButton)
      controlsContainer.appendChild(zoomOutButton)

      // eslint-disable-next-line no-undef
      google.maps.event.addDomListener(zoomInButton, 'click', () => {
        map.setZoom(map.getZoom() + 1)
      })

      // eslint-disable-next-line no-undef
      google.maps.event.addDomListener(zoomOutButton, 'click', () => {
        map.setZoom(map.getZoom() - 1)
      })

      // eslint-disable-next-line no-undef
      map.controls[google.maps.ControlPosition.LEFT_BOTTOM].push(controlsWrapper)

      map.addListener('idle', () => {
        const mapBounds = map.getBounds()
        this.setMapBounds(mapBounds)
        this.filtersIsUpdate = false
      })
      this.map = map
    })
  },
  computed: {
    ...mapState({
      visibleLocations: state => state.data.visibleLocations,
      locations: state => state.data.locations,
      searchLocations: state => state.data.search,
      currentLocation: state => state.data.currentLocation,
      currentZone: state => state.data.currentZone,
      brandFilters: state => state.data.filters,
      sources: state => state.data.sources,
      brandSettings: state => state.data.settings,
      brandZones: state => state.data.zones,
      subDivisions: state => state.data.subDivisions,

      filters: state => state.radar.filters,
      settings: state => state.radar.settings,
      search: state => state.radar.search,
      compare: state => state.radar.compare,
      stack: state => state.radar.stack,
      timeline: state => state.radar.timeline,
      mapBounds: state => state.radar.map.bounds,
      mapCenter: state => state.radar.map.center,
      mapOptions: state => state.radar.map.options,
      refreshing: state => state.radar.refreshing,
      noUi: state => state.radar.noUi,
      legends: state => state.radar.legends,

      currentSource: state => state.refine.currentSource,
      currentKpi: state => state.refine.currentKpi,
      currentDistance: state => state.refine.currentDistance,
      currentCountries: state => state.refine.currentCountries,
      activeFilters: state => state.refine.activeFilters,

      settingMarker: state => state.algolia.settings.marker,
    }),
    ...mapGetters({
      visibleLocations: 'data/visibleLocations',
      isZonesIntentionView: 'radar/isZonesIntentionView',
      isMarkersIntentionView: 'radar/isMarkersIntentionView',
      isIntentionView: 'radar/isIntentionView',
      sourcesWithAmount: 'algolia/sourcesWithAmount',
    }),
    searchLocationsCurrentKpi() {
      return this.searchLocations?.[this.currentKpi] || []
    },
    legendsAvailable() {
      if (this.isMarkersIntentionView && this.sourcesWithAmount) {
        return this.sourcesWithAmount
      }
      return []
    },
    locationRelatedToCurrentZone() {
      return (
        this.locations[this.currentKpi].find(
          location => location.internal_id === `${this.currentZone.parent_key}_${this.currentZone.key}`
        ) || {}
      )
    },
    brandZonesCurrentKPI() {
      return this.brandZones?.[this.currentKpi]
    },
    sectors() {
      return this.brandSettings.find(s => s.key === this.currentKpi).sectors
    },
    timelineKpi() {
      const timelapse = [...this.brandSettings.find(s => s.key === this.currentKpi).timelapse]
      timelapse.push(new Date().toISOString().split('T')[0])
      return timelapse || []
    },
    computedLocations() {
      const maxMarker = this.isMarkersIntentionView ? this.settingMarker.nbMaxDisplay : this.settings.marker.max
      if (this.visibleLocations.length > this.settings.marker.max) {
        return this.visibleLocations.slice(0, maxMarker)
      }
      return this.visibleLocations
    },
    computedSettings() {
      return this.brandSettings.filter(setting => setting.key === this.currentKpi)[0]
    },
    computedCompare() {
      if (this.$route.params.location) {
        return false
      }
      return true
    },
    isStackButtonDisplay() {
      return (
        (this.stack.button || this.stack.popUp) &&
        this.mapBounds &&
        !this.noUi &&
        (this.$route.name === 'zones' || this.$route.name === 'divisionZones')
      )
    },
  },
  methods: {
    ...mapActions({
      setCurrentLocation: 'data/setCurrentLocation',
      setMapBounds: 'radar/setMapBounds',
      setMapZoom: 'radar/setMapZoom',
      setCurrentZoom: 'radar/setCurrentZoom',
      toggleFilters: 'radar/toggleFilters',
      toggleSearch: 'radar/toggleSearch',
      toggleCompare: 'radar/toggleCompare',
      toggleStackPopUp: 'radar/toggleStackPopUp',
      toggleRefreshData: 'radar/toggleRefreshData',
      toggleTimeline: 'radar/toggleTimeline',
      toggleLegends: 'radar/toggleLegends',
    }),
    resetZoom() {
      this.mapFitBounds(this.map)
    },
    goToLevel1View() {
      this.$router.push({ name: 'zonesIntention' })
    },
    setZoomDefault(zoom) {
      this.setMapZoom(zoom)
    },
    refreshFilters() {
      this.filtersIsUpdate = true
      this.$emit('refreshFilters')
    },
    refreshAlgoliaFilters() {
      this.$emit('refreshAlgoliaFilters')
    },
    openSettings() {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_settings`, 'show')
      this.$modal.show('settings')
    },
    toggleElements() {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_search`, 'clicks')
      this.setCurrentLocation(null)
      this.toggleSearch(!this.search.active)
      if (this.search.active) {
        this.$refs.search.$refs.searchInput.$el.getElementsByTagName('input')[0].focus()
      }
    },
    updateMapOptions(bool) {
      if (bool) {
        this.map.setOptions({ draggable: false, scrollwheel: false, disableDoubleClickZoom: true })
        document.getElementById('map-controls').classList.add('gmap__map__zoom-controls--hidden')
        this.mapFitBounds(this.map)
      } else {
        this.map.setOptions({ draggable: true, scrollwheel: true, disableDoubleClickZoom: false })
        document.getElementById('map-controls').classList.remove('gmap__map__zoom-controls--hidden')
      }
    },
    mapFitBounds(map) {
      const bounds = new google.maps.LatLngBounds() // eslint-disable-line no-undef
      this.locations[this.currentKpi].forEach(location => {
        const geoLoc = {
          lat: parseFloat(location._geoloc.lat),
          lng: parseFloat(location._geoloc.lng),
        }
        bounds.extend(geoLoc)
      })
      if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
        const extendPoint1 = new google.maps.LatLng( // eslint-disable-line no-undef
          bounds.getNorthEast().lat() + 0.005,
          bounds.getNorthEast().lng() + 0.005
        )
        const extendPoint2 = new google.maps.LatLng( // eslint-disable-line no-undef
          bounds.getNorthEast().lat() - 0.005,
          bounds.getNorthEast().lng() - 0.005
        )
        bounds.extend(extendPoint1)
        bounds.extend(extendPoint2)
      }

      map.fitBounds(bounds)
    },
    onClickToggleStackPopUp() {
      this.toggleRefreshData(true)
      this.toggleStackPopUp(true)
    },
    onClickToggleCompare(compareActive) {
      launchGAEvent(this.$route.params.id, `${this.$route.name}_compare`, 'clicks')
      this.toggleCompare(compareActive)
    },
    onClickToggleFilters() {
      launchGAEvent(this.$route.params.id, 'analytics_filter', 'show')
      this.toggleFilters(true)
    },
  },
}
</script>

<style lang="scss">
.gmap {
  position: absolute;
  top: 0;
  width: 100%;
  height: calc(100% - #{$gmap-views-height});

  @media (min-width: $screen-sm) {
    padding-bottom: inherit;
    height: 100%;
  }

  &--noui {
    height: 100%;
  }

  &__map {
    height: 100%;

    &__zoom-controls {
      margin-bottom: calc(#{$button-min-height-mobile} + #{$gutter-sm} - 2px);
      margin-left: $gutter-sm;
      width: $button-min-height-mobile;
      height: calc(#{$button-min-height-desktop} * 2 + #{$gutter-sm});

      @media (min-width: $screen-sm) {
        margin-bottom: 134px;
        margin-left: $gutter-md;
        width: $button-min-height-desktop;
        height: calc(#{$button-min-height-desktop} * 2 + #{$gutter-md});
      }

      .gmap--noui &,
      &--hidden {
        display: none;
      }

      &__container {
        &__zoom-in,
        &__zoom-out {
          @include button-cta;
          @include button-icon;
        }

        &__zoom-in {
          margin-bottom: $spacing-sm;

          @media (min-width: $screen-sm) {
            margin-bottom: $spacing-md;
          }
        }
      }
    }
  }

  &__search {
    position: absolute;
    top: $gutter-sm;
    left: $gutter-sm;
    transition: width $transition-duration-fast $transition-effect-default;
    z-index: 2;
    width: 0;
    overflow: hidden;

    @media (min-width: $screen-sm) {
      top: $gutter-md;
      right: initial;
      left: $gutter-md;
    }

    &--left-2 {
      @media (min-width: $screen-sm) {
        left: calc(#{$button-min-height-desktop} + #{$gutter-sm} + #{$gutter-md});
      }
    }

    &--expanded {
      width: calc(100% - #{$gutter-sm * 2});
      overflow: initial;
      animation: overflow-in $transition-duration-fast $transition-effect-default;

      @media (min-width: $screen-sm) {
        width: 367px;
      }
    }
  }

  &__search-button {
    position: absolute;
    right: $gutter-sm;
    bottom: calc(#{$button-min-height-desktop} + #{$gutter-sm} + #{$gutter-md});
    transition: all $transition-duration-fast $transition-effect-default;
    z-index: 1;

    @media (min-width: $screen-sm) {
      top: $gutter-md;
      right: initial;
      left: $gutter-md;
    }

    &--shift {
      @media (min-width: $screen-sm) {
        left: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
      }

      @media (min-width: $screen-lg) {
        left: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
      }
    }

    &--left-2 {
      @media (min-width: $screen-sm) {
        left: calc(#{$button-min-height-desktop} + #{$gutter-sm} + #{$gutter-md});
      }
    }
  }

  &__compare-button {
    position: absolute;
    right: $gutter-sm;
    bottom: calc(#{$button-min-height-desktop * 2} + #{$gutter-sm} * 2 + #{$gutter-md});
    transition: all $transition-duration-fast $transition-effect-default;
    z-index: 1;

    @media (min-width: $screen-sm) {
      top: $gutter-md * 2 + $button-min-height-desktop;
      right: initial;
      left: $gutter-md;
    }

    &--swap {
      top: $gutter-md * 3 + $button-min-height-desktop * 2;
    }

    &--shift {
      @media (min-width: $screen-sm) {
        left: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
      }

      @media (min-width: $screen-lg) {
        left: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
      }
    }
  }

  &__stack-button {
    position: absolute;
    right: $gutter-sm;
    bottom: calc(#{$button-min-height-desktop * 3} + #{$gutter-sm} * 3 + #{$gutter-md});
    transition: all $transition-duration-fast $transition-effect-default;
    z-index: 1;

    @media (min-width: $screen-sm) {
      top: $gutter-md * 2 + $button-min-height-desktop;
      right: initial;
      left: $gutter-md;
    }

    &--shift {
      @media (min-width: $screen-sm) {
        left: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
      }

      @media (min-width: $screen-lg) {
        left: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
      }
    }
  }

  &__settings-button {
    position: absolute;
    bottom: $gutter-md;
    left: $gutter-sm;

    @media (min-width: $screen-sm) {
      bottom: 92px;
      left: $gutter-md;
    }
  }

  &__level-1-button {
    position: absolute;
    left: $gutter-sm;
    top: $gutter-md;
    @media (min-width: $screen-sm) {
      left: $gutter-md;
    }
  }

  &__filters-button {
    position: absolute;
    right: $gutter-sm;
    bottom: $gutter-md;

    @media (min-width: $screen-sm) {
      top: $gutter-md;
      right: $gutter-md;
      bottom: initial;
    }
  }

  &__legend-button {
    position: absolute;
    right: $gutter-sm;
    top: $gutter-sm;
    @media (min-width: $screen-sm) {
      top: calc(#{$button-min-height-desktop} + #{$gutter-sm} + #{$gutter-md});
      right: $gutter-md;
    }
  }

  &__timeline-button {
    position: absolute;
    top: $gutter-sm;
    right: $gutter-sm;
    transition: all $transition-duration-fast $transition-effect-default;

    @media (min-width: $screen-sm) {
      top: $gutter-md;
      right: calc(#{$button-min-height-desktop} + #{$gutter-md} * 2);
      bottom: initial;
    }

    &--shift {
      @media (min-width: $screen-sm) {
        right: calc(#{$gmap-sidebar-width-tablet} + #{$gutter-md} * 2);
      }

      @media (min-width: $screen-lg) {
        right: calc(#{$gmap-sidebar-width-desktop} + #{$gutter-md} * 2);
      }
    }
  }

  &__reset-button {
    position: absolute;
    top: $gutter-sm;
    left: 50%;
    transform: translateX(-50%);
    transition: top $transition-duration-fast $transition-effect-default;

    @media (min-width: $screen-sm) {
      top: $gutter-md;
    }

    &--translate {
      top: calc(#{$button-min-height-desktop} + #{$gutter-sm} * 2);

      @media (min-width: $screen-sm) {
        top: calc(#{$button-min-height-desktop} + #{$gutter-md} * 2);
      }

      @media (min-width: $screen-md) {
        top: $gutter-md;
      }
    }
  }

  &__disclaimer {
    display: none;

    @media (min-width: $screen-sm) {
      display: flex;
      position: absolute;
      right: 24px;
      bottom: 24px;
      align-items: center;
      justify-content: center;
      border-radius: 10px;
      box-shadow: 1px 2px 6px rgba($black, 0.2);
      padding: $gutter-sm / 2 $gutter-sm;
      max-width: 400px;
      min-height: $button-min-height-desktop;
      overflow: hidden;
      font-size: $font-size-sm;
    }

    &--warning {
      background-color: map-get($generic-color-variants, 'error') !important;
      color: white;
    }

    &--partial {
      background-color: $gmap-disclaimer-color-bg-partial;
    }

    &--update {
      background-color: $gmap-disclaimer-color-bg-partial-update;
    }

    &--info {
      background-color: $gmap-disclaimer-color-bg-info;
    }

    &--all {
      background-color: $gmap-disclaimer-color-bg-all;
    }
  }

  &__disclaimer-mobile {
    position: absolute;
    bottom: calc(#{$button-min-height-desktop} * 3 + #{$gutter-sm} * 3 + #{$gutter-md});
    left: $gutter-sm;

    @media (min-width: $screen-sm) {
      display: none;
    }

    &--warning {
      background-color: map-get($generic-color-variants, 'error') !important;
      border-color: map-get($generic-color-variants, 'error') !important;
      color: white;

      &:focus:not(:disabled) {
        border-color: map-get($generic-color-variants, 'error') !important;
        background-color: map-get($generic-color-variants, 'error') !important;
      }
    }

    &--partial {
      border-color: $gmap-disclaimer-color-bg-partial;
      background-color: $gmap-disclaimer-color-bg-partial;

      &:focus:not(:disabled) {
        border-color: $gmap-disclaimer-color-bg-partial;
        background-color: $gmap-disclaimer-color-bg-partial;
      }
    }

    &--update {
      border-color: $gmap-disclaimer-color-bg-partial-update;
      background-color: $gmap-disclaimer-color-bg-partial-update;
      &:focus:not(:disabled) {
        border-color: $gmap-disclaimer-color-bg-partial-update;
        background-color: $gmap-disclaimer-color-bg-partial-update;
      }
    }

    &--all {
      border-color: $gmap-disclaimer-color-bg-all;
      background-color: $gmap-disclaimer-color-bg-all;

      &:focus:not(:disabled) {
        border-color: $gmap-disclaimer-color-bg-all;
        background-color: $gmap-disclaimer-color-bg-all;
      }
    }

    &--info {
      border-color: $gmap-disclaimer-color-bg-info;
      background-color: $gmap-disclaimer-color-bg-info;

      &:focus:not(:disabled) {
        border-color: $gmap-disclaimer-color-bg-info;
        background-color: $gmap-disclaimer-color-bg-info;
      }
    }
  }
}
</style>
